import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { AddressFieldsDebounced } from "@/services/backend/addresses/components/address-fields-debounced";
import {
  updateCompanyProfileCache,
  usePatchCompanyProfileMutation,
  useShowCompanyProfileQuery,
} from "@/services/backend/company/api/company-profile";
import { CompanyProfile } from "@/services/backend/company/types/company-profile";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";

export function CompanyProfileCard({ companyId }: { companyId: string }) {
  const { data, error, isLoading } = useShowCompanyProfileQuery({ companyId });

  return (
    <div>
      {data && <CompanyProfileCardContent profile={data} />}
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <CardSkeleton />}
    </div>
  );
}

function CompanyProfileCardContent({ profile }: { profile: CompanyProfile }) {
  // The patch request carries the full profile, so the request doubles as
  // the optimistic cache value and no separate one is needed.
  const [set, { error }] = useOptimisticDebouncedMutation(
    { companyId: profile.companyId, address: profile.address },
    updateCompanyProfileCache,
    usePatchCompanyProfileMutation(),
    // All inputs debounce themselves, so the mutation does not have to.
    { delay: 0 },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Anschrift")}</CardTitle>
        <CardDescription>
          {t("Die Anschrift des Unternehmens.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AddressFieldsDebounced
          address={profile.address}
          onAddressChange={(address) =>
            set({ companyId: profile.companyId, address })
          }
          error={error}
        />
      </CardContent>
    </Card>
  );
}
